import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";
import { DrawConstant } from "$src/CGS/domain/dto/constant/DrawConstant";
import { EFrontElement } from "$src/CGS/domain/dto/core/EFrontElement";
import { EOrientation } from "$src/CGS/domain/dto/core/EOrientation";
import { AutoLength } from "$src/CGS/domain/dto/core/Length/AutoLength";
import { Length } from "$src/CGS/domain/dto/core/Length/Length";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import RangeLength from "$src/CGS/domain/dto/core/Length/RangeLength";
import { StarLength } from "$src/CGS/domain/dto/core/Length/StarLength";
import { MathUtils } from "three";
import { InsideComputation } from "./InsideComputation";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";

export class FrontElementComputation {
    public static readonly MIN_WIDTH: number = 50;
    public static readonly MIN_HEIGHT: number = 0;

    public constants: DrawConstant;
    public hasElementAbove: boolean;
    public hasElementBelow: boolean;
    public isWithGap: boolean;
    public inside: InsideComputation | null;

    public context: DrawCabinetFrontElement;
    public belowContext?: DrawCabinetFrontElement;
    public aboveContext?: DrawCabinetFrontElement;

    constructor(constants: DrawConstant, context: DrawCabinetFrontElement, belowContext?: DrawCabinetFrontElement, aboveContext?: DrawCabinetFrontElement, inside?: InsideComputation
        , hasElementAbove: boolean = false, hasElementBelow: boolean = false, isWithGap: boolean = false) {
        this.constants = constants;
        this.context = context;
        this.belowContext = belowContext;
        this.aboveContext = aboveContext;
        this.inside = inside || null;
        this.hasElementAbove = hasElementAbove ?? false;
        this.hasElementBelow = hasElementBelow ?? false;
        this.isWithGap = isWithGap;
    }

    get minWidth(): number {
        return this.getMinLength(this.context.Width, FrontElementComputation.MIN_WIDTH, this.getTotalGapSize(EOrientation.Horizontal).millimeter, this.inside?.minWidth ?? null);
    }

    get minHeight(): number {
        return this.getMinLength(this.context.Height, FrontElementComputation.MIN_HEIGHT, this.getTotalGapSize(EOrientation.Vertical).millimeter, this.inside?.minHeight ?? null);
    }

    get maxWidth(): number | null {
        return this.getMaxLength(this.context.Width, this.getTotalGapSize(EOrientation.Horizontal).millimeter, this.inside?.maxWidth ?? null);
    }

    get maxHeight(): number | null {
        return this.getMaxLength(this.context.Height, this.getTotalGapSize(EOrientation.Vertical).millimeter, this.inside?.maxHeight ?? null);
    }

    getMin(orientation: EOrientation): number {
        return orientation === EOrientation.Horizontal ? this.minWidth : this.minHeight;
    }

    getMax(orientation: EOrientation): number | null {
        return orientation === EOrientation.Horizontal ? this.maxWidth : this.maxHeight;
    }

    gapWithAboveElement(orientation: EOrientation): LengthValue {
        if (orientation === EOrientation.Horizontal) {
            return this.constants.SideBoxGap;
        } else {
            if (this.aboveContext?.FrontElement === EFrontElement.G) {
                return new LengthValueMillimeter(0);
            } else {
                return new LengthValueMillimeter(this.constants.GapBetweenFrontElements.millimeter);
            }
        }
    }

    getTotalGapSize(orientation: EOrientation): LengthValue {
        if (orientation === EOrientation.Horizontal) {
            return LengthMaths.multiply(this.constants.SideBoxGap, 2);
        } else {
            if (this.context?.FrontElement === EFrontElement.G) {
                return new LengthValueMillimeter((this.hasElementBelow ? 0 : this.constants.GapBottomBox.millimeter) + (this.hasElementAbove ? 0 : this.constants.GapTopBox.millimeter));
            } else if (this.belowContext?.FrontElement === EFrontElement.G) {
                return new LengthValueMillimeter((this.hasElementBelow ? 0 : this.constants.GapBottomBox.millimeter) + (this.hasElementAbove ? 0 : this.constants.GapTopBox.millimeter));
            } else {
                return new LengthValueMillimeter((this.hasElementBelow ? this.constants.GapBetweenFrontElements.millimeter : this.constants.GapBottomBox.millimeter) + (this.hasElementAbove ? 0 : this.constants.GapTopBox.millimeter));
            }
        }
    }

    getSizeLengthValue(orientation: EOrientation): LengthValue {
        return new LengthValueMillimeter(this.getMin(orientation));
    }

    isFixed(orientation: EOrientation): boolean {
        return this.getMin(orientation) === this.getMax(orientation);
    }

    private getMinLength(length: Length, defaultMin: number, gaps: number, insideMin: number | null): number {
        if (this.isWithGap) {
            gaps = 0;
        }

        if (length instanceof RangeLength) {
            return MathUtils.clamp(insideMin ?? 0, length.start.millimeter, length.stop.millimeter) - gaps;
        } else if (length instanceof StarLength || length instanceof AutoLength) {
            return defaultMin;
        } else {
            return LengthMaths.getLengthValue(length).millimeter - gaps;
        }
    }

    private getMaxLength(length: Length, gaps: number, insideMax: number | null): number | null {
        if (this.isWithGap) {
            gaps = 0;
        }

        if (length instanceof RangeLength) {
            return MathUtils.clamp(insideMax ?? Number.MAX_VALUE, length.start.millimeter, length.stop.millimeter) - gaps;
        } else if (length instanceof StarLength || length instanceof AutoLength) {
            return null;
        } else {
            return LengthMaths.getLengthValue(length).millimeter - gaps;
        }
    }
}